/**
 * The next few days a delivery can actually be scheduled.
 *
 * Powers the quick-pick row on order entry, so a counter taking a phone order
 * for "tomorrow" taps once instead of opening a date picker — which is the
 * common case by a wide margin.
 *
 * A day is offered only if the shop is open AND it is not blacked out. Both
 * exclusions are about capacity to fulfil, so neither should ever be silently
 * skipped: the quick row would otherwise suggest a date the order cannot be
 * delivered on. The picker behind "Other date" imposes no such restriction —
 * staff can still choose a closed Sunday when they know something we don't.
 */

const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Local-calendar YYYY-MM-DD. Deliberately not toISOString(), which is UTC and
 *  rolls the date over for anyone west of Greenwich in the evening. */
export const toDateKey = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const isClosedOn = (date, storeHours) => {
  if (!storeHours) return false; // no hours configured — assume open rather than block everything
  const entry = storeHours[DAY_KEYS[date.getDay()]];
  return !!entry?.closed;
};

export const isBlackedOut = (date, blackoutDates) => {
  if (!blackoutDates?.length) return false;
  const key = toDateKey(date);
  return blackoutDates.some((b) => (typeof b === 'string' ? b : b?.date) === key);
};

/** 'YYYY-MM-DD' plus n days, on the local calendar. */
export const addDaysKey = (key, n) => {
  const [y, m, d] = key.split('-').map(Number);
  return toDateKey(new Date(y, m - 1, d + n));
};

/**
 * The first day an order placed now can go out, as 'YYYY-MM-DD'.
 *
 * @param {object} opts
 * @param {string} opts.today       'YYYY-MM-DD' in the shop's timezone
 * @param {string} [opts.nowHHMM]   'HH:MM' in the shop's timezone
 * @param {string} [opts.cutoffTime] 'HH:MM' — after it, same-day is gone
 * @param {number} [opts.leadDays]  days of notice (shop default or the slowest item)
 */
export const earliestDeliveryDay = ({ today, nowHHMM = null, cutoffTime = null, leadDays = 0 } = {}) => {
  let days = Math.max(0, Number(leadDays) || 0);
  if (days === 0 && cutoffTime && nowHHMM && nowHHMM >= cutoffTime) days = 1;
  return addDaysKey(today, days);
};

/** Capacity for one day from the rules payload: { limit, used, remaining, full } or null. */
export const capacityFor = (dateKey, capacity) => {
  if (!Array.isArray(capacity)) return null;
  const row = capacity.find((c) => c.date === dateKey);
  if (!row) return null;
  const remaining = Math.max(0, Number(row.limit) - Number(row.used || 0));
  return { limit: Number(row.limit), used: Number(row.used || 0), remaining, full: remaining <= 0 };
};

/**
 * @param {object} opts
 * @param {Date}   opts.from        day to start from, inclusive (default today)
 * @param {Array}  [opts.capacity]  rules.capacity — [{ date, limit, used }]; days get `full` / `remaining`
 * @param {object} opts.storeHours  { monday: { closed }, ... }
 * @param {Array}  opts.blackoutDates  ['YYYY-MM-DD'] or [{ date, reason }]
 * @param {number} opts.count       how many days to offer
 * @param {number} opts.maxLookahead safety bound, so a shop marked closed every
 *                                   day cannot spin this forever
 */
export const upcomingDeliveryDays = ({
  from = new Date(),
  storeHours = null,
  blackoutDates = [],
  capacity = null,
  count = 6,
  maxLookahead = 60,
} = {}) => {
  const days = [];
  const cursor = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const todayKey = toDateKey(new Date());

  for (let i = 0; i < maxLookahead && days.length < count; i += 1) {
    const d = new Date(cursor);
    d.setDate(cursor.getDate() + i);

    if (isClosedOn(d, storeHours) || isBlackedOut(d, blackoutDates)) continue;

    const key = toDateKey(d);
    const cap = capacityFor(key, capacity);
    days.push({
      key,
      date: d,
      full: !!(cap && cap.full),
      remaining: cap ? cap.remaining : null,
      dayLabel: DAY_LABELS[d.getDay()],
      dayNumber: d.getDate(),
      // Only TODAY gets a word. "Tomorrow" was tried and dropped: at 84px against
      // 52px for every other button it broke the row's rhythm, and the weekday
      // is just as clear when it sits directly after Today.
      relativeLabel: key === todayKey ? 'Today' : null,
      isToday: key === todayKey,
    });
  }

  return days;
};

export default upcomingDeliveryDays;
